import { Injectable } from '@angular/core';
import { ConfirmationService } from 'primeng/api';

export interface DeleteConfirmOptions {
  resourceType: string;        // e.g., "Questionnaire", "Form", "Virtual Field"
  itemName?: string;           // For single item delete
  itemCount?: number;          // For batch delete (e.g., 5)
  additionalInfo?: string;     // Optional additional information to display
  customMessage?: string;      // Optional custom message override
  onConfirm: () => void;       // Callback when Delete is clicked
  onCancel?: () => void;       // Optional callback when Cancel is clicked
}

export interface DiscardConfirmOptions {
  customMessage?: string;      // Optional custom message override
  onConfirm: () => void;       // Callback when Discard is clicked
  onCancel?: () => void;       // Optional callback when Cancel is clicked
}

interface StructuredMessage {
  question: string;
  itemName: string;
  additionalInfo?: string;
}

@Injectable({
  providedIn: 'root'
})
export class GiPConfirmService {

  constructor(private confirmationService: ConfirmationService) { }

  /**
   * Shows a delete confirmation dialog following best practices
   */
  confirmDelete(options: DeleteConfirmOptions): void {
    const header = this.buildHeader(options);
    const messageData = this.buildMessage(options);

    const confirmation: any = {
      key: "deleteConfirm",
      header: header,
      message: typeof messageData === 'string' ? messageData : '',
      accept: options.onConfirm,
      reject: options.onCancel
    };

    // Add structured data for template rendering
    if (typeof messageData === 'object') {
      confirmation.resourceType = options.resourceType;
      confirmation.itemName = messageData.itemName;
      confirmation.additionalInfo = messageData.additionalInfo;
    }

    this.confirmationService.confirm(confirmation);
  }

  /**
   * Shows a discard confirmation dialog for unsaved changes
   */
  confirmDiscard(options: DiscardConfirmOptions): void {
    const header = 'Confirm Discard';
    const message = options.customMessage ||
      'You have unsaved changes. Are you sure you want to discard them?';

    this.confirmationService.confirm({
      key: "discardConfirm",
      header: header,
      message: message,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        severity: 'secondary',
      },
      accept: options.onConfirm,
      reject: options.onCancel
    });
  }

  private buildHeader(options: DeleteConfirmOptions): string {
    const { resourceType, itemCount } = options;
    const pluralizedType = itemCount && itemCount > 1
      ? this.pluralize(resourceType)
      : resourceType;
    return `Delete ${pluralizedType}`;
  }

  private buildMessage(options: DeleteConfirmOptions): string | StructuredMessage {
    const { resourceType, itemName, itemCount, additionalInfo } = options;

    if (itemCount && itemCount > 1) {
      // Batch delete message - keep as string for backward compatibility
      return `Are you sure you want to delete ${itemCount} items? This action cannot be undone.`;
    } else if (itemName) {
      // Single item delete message - return structured object for template
      return {
        question: `Are you sure you want to delete the following ${resourceType}?`,
        itemName: itemName,
        additionalInfo: additionalInfo
      };
    } else {
      // Fallback message
      return 'Are you sure you want to proceed with this delete action? This action cannot be undone.';
    }
  }

  private pluralize(word: string): string {
    // Simple pluralization rules - can be extended as needed
    if (word.endsWith('y')) {
      return word.slice(0, -1) + 'ies';
    } else if (word.endsWith('s') || word.endsWith('sh') || word.endsWith('ch') || word.endsWith('x') || word.endsWith('z')) {
      return word + 'es';
    } else {
      return word + 's';
    }
  }
}
